import { useEffect, useState } from "react";
import { X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "lamka_install_dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const recentlyDismissed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < 14 * 24 * 3600 * 1000;

export const useInstall = () => {
  const [canPrompt, setCanPrompt] = useState(!!window.__lamkaInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const on = () => setCanPrompt(true);
    const done = () => { setInstalled(true); setCanPrompt(false); };
    window.addEventListener("lamka:installable", on);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("lamka:installable", on); window.removeEventListener("appinstalled", done); };
  }, []);
  const prompt = async () => {
    const p = window.__lamkaInstallPrompt;
    if (!p) return false;
    p.prompt();
    const { outcome } = await p.userChoice;
    window.__lamkaInstallPrompt = null;
    setCanPrompt(false);
    if (outcome === "accepted") setInstalled(true);
    return true;
  };
  return { canPrompt, installed, ios: isIOS(), prompt };
};

export const InstallPrompt = () => {
  const { canPrompt, installed, ios, prompt } = useInstall();
  const [show, setShow] = useState(false);
  const [steps, setSteps] = useState(false);

  useEffect(() => {
    if (installed || recentlyDismissed() || !(canPrompt || ios)) return;
    const t = setTimeout(() => setShow(true), 6000);
    return () => clearTimeout(t);
  }, [canPrompt, ios, installed]);

  useEffect(() => {
    const open = () => setSteps(true);
    window.addEventListener("lamka:show-install-steps", open);
    return () => window.removeEventListener("lamka:show-install-steps", open);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (canPrompt) {
      await prompt();
      setShow(false);
    } else {
      setShow(false);
      setSteps(true);
    }
  };

  return (
    <>
      {show && !installed && (
        <div data-testid="install-banner" className="fixed bottom-0 inset-x-0 z-40 sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-sm bg-[#0F0F0F] text-white border-t sm:border border-[#C5A059]/50 px-4 pt-4 pb-safe sm:pb-4 flex items-center gap-3 fade-up">
          <img src="/icon-192.png" alt="" className="h-11 w-11 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Get the Lamka Online app</p>
            <p className="text-xs text-white/55">Add it to your home screen — opens full-screen, no download.</p>
          </div>
          <button data-testid="install-banner-add" onClick={install} className="h-10 px-4 shrink-0 bg-[#C5A059] text-[#0F0F0F] text-[10px] tracking-[0.25em] uppercase">Add</button>
          <button data-testid="install-banner-dismiss" aria-label="Not now" onClick={dismiss} className="h-10 w-8 shrink-0 flex items-center justify-center text-white/50 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
      )}
      {steps && (
        <div data-testid="install-steps" className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setSteps(false)}>
          <div className="w-full sm:max-w-sm bg-[#FBF9F5] p-6 pb-safe fade-up" onClick={(e) => e.stopPropagation()}>
            <p className="eyebrow mb-2">Add to home screen</p>
            <h3 className="font-display text-2xl mb-5">Lamka Online on your phone</h3>
            {ios ? (
              <ol className="space-y-4 text-sm">
                <li className="flex items-center gap-3"><Share className="h-5 w-5 text-[#C5A059] shrink-0" /> Tap the <b>Share</b> button in Safari's toolbar.</li>
                <li className="flex items-center gap-3"><PlusSquare className="h-5 w-5 text-[#C5A059] shrink-0" /> Choose <b>Add to Home Screen</b>, then tap <b>Add</b>.</li>
              </ol>
            ) : (
              <p className="text-sm">Open your browser menu (⋮) and choose <b>Install app</b> or <b>Add to Home screen</b>.</p>
            )}
            <button data-testid="install-steps-close" onClick={() => setSteps(false)} className="mt-6 w-full bg-[#0F0F0F] text-[#C5A059] py-3 text-xs tracking-[0.3em] uppercase">Got it</button>
          </div>
        </div>
      )}
    </>
  );
};

export const InstallLink = () => {
  const { canPrompt, installed, prompt } = useInstall();
  if (installed) return null;
  return (
    <button
      data-testid="footer-install-button"
      onClick={async () => { if (!(canPrompt && (await prompt()))) window.dispatchEvent(new Event("lamka:show-install-steps")); }}
      className="inline-flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-5 py-3 text-[10px] tracking-[0.3em] uppercase hover:bg-[#C5A059] hover:text-[#0F0F0F] transition-colors"
    >
      Add to home screen
    </button>
  );
};
